<template>
  <div class="min-h-screen">
    <div class="max-w-7xl mx-auto p-4 sm:p-6 md:p-8">
      <header class="mb-8">
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 class="text-3xl sm:text-4xl font-bold tracking-tight text-slate-800 dark:text-slate-100">
              Minha <span class="bg-clip-text text-transparent bg-gradient-to-r from-brand-500 to-brand-400">Assinatura</span>
            </h1>
            <p class="mt-2 text-sm text-slate-600 dark:text-slate-400">
              Gerencie plano, cobrança e cancelamento com segurança pelo portal Stripe.
            </p>
          </div>
          <div class="flex-shrink-0" v-if="isActive">
            <button @click="abrirPortal" :disabled="billingStore.loadingPortal" class="btn-primary w-full sm:w-auto flex items-center justify-center">
              <span class="material-symbols-outlined mr-2 text-lg">open_in_new</span>
              Gerenciar Cobrança
            </button>
          </div>
        </div>
      </header>

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        <section class="lg:col-span-2 card p-0 overflow-hidden">
          
          <div class="p-6 sm:p-8">
            <div class="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Plano atual</div>
            <div class="mt-1 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div class="text-xl sm:text-2xl font-semibold tracking-tight text-slate-800 dark:text-slate-100">
                <span v-if="isActive">{{ planoLabel }}</span>
                <span v-else class="text-slate-400 dark:text-slate-500 italic">Nenhum ativo</span>
              </div>
              <span class="badge flex-shrink-0 self-start" :class="chipMeta.class">
                <span class="inline-block size-1.5 rounded-full mr-1.5" :class="chipMeta.dot"></span>
                {{ statusText }}
              </span>
            </div>
          </div>

          <div class="p-6 sm:p-8 border-t border-slate-100 dark:border-brand-700/50">
            <h3 class="text-base font-semibold text-slate-800 dark:text-slate-100 mb-4">
              Detalhes da Cobrança
            </h3>
            <dl class="grid grid-cols-1 sm:grid-cols-2 gap-4 text-[13px]">
              <div class="stat-row" v-if="nextChargeLabel">
                <dt>Próxima renovação</dt>
                <dd>{{ nextChargeLabel }}</dd>
              </div>
              <div class="stat-row">
                <dt>Status cobrança</dt>
                <dd>{{ statusTextFull }}</dd>
              </div>
            </dl>
          </div>

          <div class="p-6 sm:p-8 bg-slate-50/50 dark:bg-brand-900/20">
             <h3 class="text-base font-semibold text-slate-800 dark:text-slate-100 mb-4">
              Ações
            </h3>
            <div class="grid gap-3 sm:grid-cols-2">
              <button
                v-if="isActive"
                @click="abrirPortal"
                :disabled="billingStore.loadingPortal"
                class="btn-primary flex items-center justify-center"
              >
                <span class="material-symbols-outlined mr-2 text-lg">credit_card</span>
                Editar forma de pagamento
              </button>

              <button
                v-if="isActive"
                @click="abrirPortal"
                :disabled="billingStore.loadingPortal"
                class="btn-secondary flex items-center justify-center"
              >
                <span class="material-symbols-outlined mr-2 text-lg">swap_horiz</span>
                Alterar plano (mensal ↔ anual)
              </button>

              <router-link
                v-else
                :to="{ name: 'Planos' }"
                class="btn-primary flex items-center justify-center text-center"
              >
                <span class="material-symbols-outlined mr-2 text-lg">arrow_forward</span>
                Assinar agora
              </router-link>
            </div>
             <p v-if="billingStore.error" class="mt-4 text-[12px] text-red-600 dark:text-red-400 text-center">
              {{ billingStore.error }}
            </p>
          </div>

          <div class="p-4 sm:p-6 border-t border-slate-100 dark:border-brand-700/50">
            <div class="flex items-center justify-between text-[12px] text-slate-500 dark:text-slate-400">
              <div class="flex items-center gap-1.5">
                <span class="material-symbols-outlined text-base">lock</span>
                <span>Portal Stripe seguro</span>
              </div>
              <div>Cancele quando quiser! Sem complicações.</div>
            </div>
          </div>
        </section>

        <aside class="card p-0 overflow-hidden">
          <div class="p-6 sm:p-7 border-b border-slate-100 dark:border-slate-800">
            <h3 class="text-lg font-semibold text-slate-800 dark:text-slate-100">Dúvidas rápidas</h3>
            <p class="mt-1 text-sm text-slate-600 dark:text-slate-400">
              Suas alterações entram em vigor imediatamente após confirmar no portal.
            </p>
          </div>
          <ul class="divide-y divide-slate-100 dark:divide-slate-800">
            <li class="faq-row">
              <div>
                <div class="faq-q">Como troco o cartão?</div>
                <div class="faq-a">Clique em “Editar forma de pagamento”.</div>
              </div>
              <button @click="abrirPortal" class="btn-ghost" title="Abrir portal">
                <span class="material-symbols-outlined text-base">open_in_new</span>
              </button>
            </li>
            <li class="faq-row">
              <div>
                <div class="faq-q">Como mudo para anual?</div>
                <div class="faq-a">Use “Alterar plano (mensal ↔ anual)”.</div>
              </div>
              <button @click="abrirPortal" class="btn-ghost" title="Abrir portal">
                <span class="material-symbols-outlined text-base">swap_horiz</span>
              </button>
            </li>
            <li class="faq-row">
              <div>
                <div class="faq-q">Como cancelar?</div>
                <div class="faq-a">No portal Stripe, opção “Cancelar assinatura”.</div>
              </div>
              <button @click="abrirPortal" class="btn-ghost" title="Abrir portal">
                <span class="material-symbols-outlined text-base">cancel</span>
              </button>
            </li>
          </ul>
        </aside>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue'
import { useBillingStore } from '@/stores/billing'
import { useAuthStore } from '@/stores/auth'
import { useRouter } from 'vue-router'

const billingStore = useBillingStore()
const authStore = useAuthStore()
const router = useRouter()

const isActive = computed(() => {
  const s = billingStore.status.statusAssinatura
  if (!s) return false
  const up = s.toUpperCase()
  return up === 'ACTIVE' || up === 'TRIALING' || up === 'PAST_DUE' || up === 'ATIVA' || up === 'TRIAL'
})

const planoLabel = computed(() => billingStore.status.plano || '—')

const statusText = computed(() => {
  const s = (billingStore.status.statusAssinatura || '').toUpperCase()
  if (s === 'ACTIVE' || s === 'ATIVA') return 'ativa'
  if (s === 'TRIALING' || s === 'TRIAL') return 'trial'
  if (s === 'PAST_DUE' || s === 'PENDENTE_PAGAMENTO') return 'pendente'
  if (s === 'CANCELED' || s === 'CANCELADA') return 'cancelada'
  return s ? s.toLowerCase() : 'indefinido'
})

const statusTextFull = computed(() => {
  const s = (billingStore.status.statusAssinatura || '').toUpperCase()
  if (s === 'ACTIVE' || s === 'ATIVA') return 'Cobrando normalmente'
  if (s === 'TRIALING' || s === 'TRIAL') return 'Período de avaliação'
  if (s === 'PAST_DUE' || s === 'PENDENTE_PAGAMENTO') return 'Pagamento pendente'
  if (s === 'CANCELED' || s === 'CANCELADA') return 'Cancelada'
  return 'Indefinido'
})

const chipMeta = computed(() => {
  const t = statusText.value
  if (t === 'ativa') return { class: 'badge-emerald', dot: 'bg-emerald-400' }
  if (t === 'trial') return { class: 'badge-sky', dot: 'bg-sky-400' }
  if (t === 'pendente') return { class: 'badge-amber', dot: 'bg-amber-400' }
  if (t === 'cancelada') return { class: 'badge-rose', dot: 'bg-rose-400' }
  return { class: 'badge-slate', dot: 'bg-slate-400' }
})

const nextChargeLabel = computed(() => {
  if (!billingStore.status.currentPeriodEnd) return ''
  const d = new Date(billingStore.status.currentPeriodEnd)
  if (isNaN(d.getTime())) return ''
  const dia = String(d.getDate()).padStart(2, '0')
  const mes = String(d.getMonth() + 1).padStart(2, '0')
  const ano = d.getFullYear()
  return `${dia}/${mes}/${ano}`
})

const abrirPortal = async () => {
  if (!authStore.token) {
    router.push({ name: 'Login' })
    return
  }
  await billingStore.openPortal()
}

onMounted(async () => {
  if (!authStore.token) {
    router.push({ name: 'Login' })
    return
  }
  await billingStore.fetchStatus()
})
</script>

<style scoped>
.card { 
  @apply bg-white dark:bg-brand-800 rounded-2xl shadow-lg border border-slate-100 dark:border-brand-700/50
  transition-all duration-300 ease-in-out; 
}
.btn-primary { 
  @apply px-5 py-2.5 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg 
  shadow-lg shadow-brand-500/20 hover:shadow-brand-500/30 
  disabled:opacity-60 disabled:cursor-not-allowed
  transform hover:-translate-y-0.5 transition-all duration-300 ease-in-out;
}
.btn-secondary { 
  @apply px-5 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 bg-slate-100 hover:bg-slate-200 
  dark:bg-slate-800 dark:hover:bg-slate-700 rounded-lg 
  transform hover:-translate-y-0.5 transition-all duration-300 ease-in-out;
}
.btn-ghost { 
  @apply p-2 rounded-lg text-slate-600 dark:text-slate-300 
  hover:bg-slate-100 dark:hover:bg-slate-800 
  transform transition-all duration-300 ease-in-out;
}
.btn-ghost:hover {
  @apply rotate-12;
}

.badge { @apply inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold leading-none border; }
.badge-emerald { @apply border-emerald-400/30 text-emerald-600 dark:text-emerald-300 bg-emerald-500/10; }
.badge-sky { @apply border-sky-400/30 text-sky-600 dark:text-sky-300 bg-sky-500/10; }
.badge-amber { @apply border-amber-400/30 text-amber-700 dark:text-amber-300 bg-amber-500/10; }
.badge-rose { @apply border-rose-400/30 text-rose-600 dark:text-rose-300 bg-rose-500/10; }
.badge-slate { @apply border-slate-400/30 text-slate-600 dark:text-slate-300 bg-slate-500/10; }

.stat-row { 
  @apply bg-slate-50/70 dark:bg-brand-900/40 rounded-lg px-3 py-2 border border-slate-100 dark:border-brand-700/50
  transition-all duration-300 ease-in-out;
}
.stat-row:hover {
  @apply shadow-md scale-[1.02] bg-slate-100 dark:bg-brand-700/60;
}
.stat-row dt { @apply text-slate-500 dark:text-slate-400; }
.stat-row dd { @apply font-semibold text-slate-800 dark:text-slate-100; }

.faq-row { 
  @apply p-4 flex items-center justify-between
  transition-colors duration-300 ease-in-out
  hover:bg-slate-50 dark:hover:bg-brand-700/30;
}
.faq-q { @apply text-sm font-medium text-slate-800 dark:text-slate-100; }
.faq-a { @apply text-xs text-slate-600 dark:text-slate-400; }
</style>